import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "button",
    "dropdown",
    "arrow",
    "searchInput",
    "optionsList",
    "option",
    "checkbox",
    "selectedText",
    "hiddenInput",
    "noResults"
  ]

  connect() {
    this.selectedValues = []
    this.isOpen = false

    const existingInputs = this.element.querySelectorAll('input[type="hidden"][name="' + this.hiddenInputTarget.name + '"]:not([data-main-input="true"])')
    if (existingInputs.length > 0) {
      this.selectedValues = Array.from(existingInputs).map(input => input.value).filter(v => v.trim() !== '')
    } else {
      const initialValue = this.hiddenInputTarget.value
      if (initialValue) {
        this.selectedValues = initialValue.split(',').filter(v => v.trim() !== '')
      }
    }

    this.updateDisplay()
    this.updateCheckboxes()

    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside.bind(this))
  }

  disconnect() {
    document.removeEventListener('click', this.handleClickOutside.bind(this))
  }

  toggleDropdown(event) {
    event.stopPropagation()

    if (this.isOpen) {
      this.closeDropdown()
    } else {
      this.openDropdown()
    }
  }

  openDropdown() {
    this.dropdownTarget.classList.remove('hidden')
    this.arrowTarget.style.transform = 'rotate(180deg)'
    this.isOpen = true

    // Focus search input when opening
    setTimeout(() => {
      this.searchInputTarget.focus()
    }, 100)
  }

  closeDropdown() {
    this.dropdownTarget.classList.add('hidden')
    this.arrowTarget.style.transform = 'rotate(0deg)'
    this.isOpen = false

    // Clear search when closing
    this.searchInputTarget.value = ''
    this.filterOptions()
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.closeDropdown()
    }
  }

  filterOptions() {
    const searchTerm = this.searchInputTarget.value.toLowerCase()
    let visibleCount = 0

    this.optionTargets.forEach((option, index) => {
      const label = option.dataset.label.toLowerCase()
      const isVisible = label.includes(searchTerm)

      option.style.display = isVisible ? 'flex' : 'none'
      if (isVisible) visibleCount++
    })

    // Show/hide no results message
    if (visibleCount === 0 && searchTerm !== '') {
      this.noResultsTarget.classList.remove('hidden')
    } else {
      this.noResultsTarget.classList.add('hidden')
    }
  }

  toggleOption(event) {
    let option, checkbox

    // Check if the clicked element is the checkbox itself
    if (event.currentTarget.type === 'checkbox') {
      checkbox = event.currentTarget
      option = checkbox.closest('[data-uikit--select-multi-target="option"]')
      // Don't stop propagation for checkbox clicks to allow natural behavior
    } else {
      event.stopPropagation()
      option = event.currentTarget
      checkbox = option.querySelector('input[type="checkbox"]')
      // Toggle checkbox state manually for non-checkbox clicks
      checkbox.checked = !checkbox.checked
    }

    const value = option.dataset.value
    const label = option.dataset.label

    // Read the current checkbox state (after natural toggle for checkbox clicks)
    if (checkbox.checked) {
      // Add to selection if not already present
      if (!this.selectedValues.includes(value)) {
        this.selectedValues.push(value)
      }
    } else {
      // Remove from selection
      this.selectedValues = this.selectedValues.filter(v => v !== value)
    }

    this.updateDisplay()
    this.updateHiddenInput()
  }

  updateDisplay() {
    const selectedOptions = this.optionTargets.filter(option =>
      this.selectedValues.includes(option.dataset.value)
    )

    if (selectedOptions.length === 0) {
      this.selectedTextTarget.innerHTML = '<span class="text-neutral-500">Select options...</span>'
    } else if (selectedOptions.length <= 2) {
      const labels = selectedOptions.map(option => option.dataset.label)
      this.selectedTextTarget.textContent = labels.join(', ')
    } else {
      const firstLabel = selectedOptions[0].dataset.label
      this.selectedTextTarget.textContent = `${firstLabel} +${selectedOptions.length - 1} more`
    }
  }

  updateCheckboxes() {
    this.optionTargets.forEach(option => {
      const checkbox = option.querySelector('input[type="checkbox"]')
      const value = option.dataset.value
      checkbox.checked = this.selectedValues.includes(value)
    })
  }

    updateHiddenInput() {
    // Clear existing dynamic hidden inputs (but not the main one)
    const existingInputs = this.element.querySelectorAll('input[type="hidden"][name="' + this.hiddenInputTarget.name + '"]:not([data-main-input="true"])')
    existingInputs.forEach(input => input.remove())

    // Create new hidden inputs for each selected value (array format)
    this.selectedValues.forEach(value => {
      const hiddenInput = document.createElement('input')
      hiddenInput.type = 'hidden'
      hiddenInput.name = this.hiddenInputTarget.name
      hiddenInput.value = value
      this.element.appendChild(hiddenInput)
    })

    this.hiddenInputTarget.value = ''

    // Disable the main hidden input when there are no selections to prevent empty values from being submitted
    if (this.selectedValues.length === 0) {
      this.hiddenInputTarget.disabled = true
    } else {
      this.hiddenInputTarget.disabled = false
    }

    // Trigger change event for form handling
    const event = new Event('change', { bubbles: true, cancelable: true })
    this.hiddenInputTarget.dispatchEvent(event)

    // Also trigger input event for broader compatibility
    const inputEvent = new Event('input', { bubbles: true, cancelable: true })
    this.hiddenInputTarget.dispatchEvent(inputEvent)
  }

  // Public method to get selected values
  getSelectedValues() {
    return this.selectedValues
  }

  // Public method to set selected values
  setSelectedValues(values) {
    this.selectedValues = values

    // Update checkboxes
    this.optionTargets.forEach(option => {
      const checkbox = option.querySelector('input[type="checkbox"]')
      const value = option.dataset.value
      checkbox.checked = this.selectedValues.includes(value)
    })

    this.updateDisplay()
    this.updateHiddenInput()
  }

  // Public method to clear all selections
  clear() {
    this.selectedValues = []
    this.checkboxTargets.forEach(checkbox => checkbox.checked = false)
    this.updateDisplay()
    this.updateHiddenInput()

    // Trigger a custom event to notify parent controllers that selections were cleared
    const clearEvent = new CustomEvent('selectionsCleared', {
      detail: { component: this },
      bubbles: true
    })
    this.element.dispatchEvent(clearEvent)
  }

  // Prevent event propagation when clicking checkbox directly
  preventPropagation(event) {
    event.stopPropagation()
  }
}
